/**
 * Client-side error handling for the Auth_Service API (task 21.1).
 *
 * Every service on the platform returns a consistent JSON error envelope
 * (see the shared `error-envelope` module and the design's "Error Handling"):
 *
 * ```json
 * { "error": { "code": "INVALID_OTP", "message": "...", "field": "code", "requestId": "..." } }
 * ```
 *
 * This module mirrors that shape on the browser side and provides
 * {@link ApiError} — a typed `Error` carrying the HTTP status and the parsed
 * envelope fields — so views and the auth context can branch on a stable
 * machine-readable `code` and surface the non-sensitive `message` to the user.
 */

/**
 * Machine-readable error codes the Auth_Service can return. Kept in sync with
 * the shared `ErrorCode` taxonomy; only the auth-relevant members are needed
 * here, and an open `(string & {})` fallback keeps forward compatibility if the
 * backend adds a code the SPA has not yet learned about.
 */
export type ApiErrorCode =
  | 'AUTH_REQUIRED'
  | 'SESSION_EXPIRED'
  | 'INVALID_OTP'
  | 'OTP_EXPIRED'
  | 'INVALID_CREDENTIALS'
  | 'ACCOUNT_LOCKED'
  | 'OTP_DELIVERY_FAILED'
  | 'NOT_AUTHORIZED'
  | 'SERVICE_UNAVAILABLE'
  | 'INTERNAL_ERROR'
  // Open fallback preserving literal autocomplete for codes not yet known to
  // the client (the `& {}` keeps the union open without widening to `string`).
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/** The inner error object of the standard envelope. */
export interface ApiErrorBody {
  readonly code: ApiErrorCode;
  readonly message: string;
  readonly field?: string;
  readonly requestId?: string;
}

/** The standard error envelope wrapping the inner {@link ApiErrorBody}. */
export interface ApiErrorEnvelope {
  readonly error: ApiErrorBody;
}

/**
 * A typed error thrown by the API client for any non-2xx response (or a
 * transport-level failure). Carries the HTTP `status` and the parsed envelope
 * fields so callers can branch on `code` and render `message`.
 */
export class ApiError extends Error {
  /** HTTP status code (0 when the request never reached the server). */
  readonly status: number;
  /** Machine-readable, stable error code from the taxonomy. */
  readonly code: ApiErrorCode;
  /** The input field the error is attributable to, when applicable. */
  readonly field: string | undefined;
  /** Correlation id linking the error to server logs/traces, when available. */
  readonly requestId: string | undefined;

  constructor(
    status: number,
    body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.field = body.field;
    this.requestId = body.requestId;
    // Restore the prototype chain for instanceof under transpiled targets.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Type guard: narrow a caught value to {@link ApiError}. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Narrow an unknown parsed JSON body to an {@link ApiErrorEnvelope}. Tolerant
 * of partial/malformed payloads so a broken error response never crashes the
 * client — callers fall back to a generic message when this returns `false`.
 */
export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}
